import { useMetaLang, MetaLang } from '../../i18n/metaReviewLang';
import type {
  RecipeVideoComposePhase,
  RecipeVideoStageKey,
  RecipeVideoStageState,
  RecipeVideoStatus,
  RecipeVideoStep,
} from '../../services/api/recipeVideoService';

/**
 * 動画スタイルレシピ画面の文言（日本語・英語）。
 * アプリ共通の言語切り替え（metaReviewLang）に合わせる。
 */

/** 経過時間（秒）を「3分05秒」のように。1時間を超えたら秒は出さない */
const splitElapsed = (total: number) => {
  const s = Math.max(0, Math.floor(total));
  return { h: Math.floor(s / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
};
const pad2 = (n: number) => String(n).padStart(2, '0');

const JA = {
  pageTitle: 'スタイル動画（β）',
  createTitle: '商品とスタイルを選んで動画を作る',
  createLead: '台本・絵・声・BGM・字幕までAIが自動で作ります。できた動画はシーンごとに直せます。',
  product: '商品',
  productPlaceholder: '商品を選んでください',
  noProducts: '商品が登録されていません。先に商品を登録してください。',
  style: '動画のスタイル',
  noStyles: '公開中のスタイルがありません。',
  scenes: (n: number) => `${n}シーン`,
  approxSeconds: (n: number) => `約${n}秒〜`,
  estimate: (n: number) => `目安 ${n.toLocaleString()} クレジット`,
  appeal: '訴求（任意）',
  appealAuto: 'おまかせ（商品の最初の訴求）',
  productInfo: '商品情報',
  selectProduct: '商品を選択',
  changeProduct: '商品を変更',
  appealLabel: '誰に、何を伝えるか',
  appealNote: '商品を選ぶ画面の「誰に、何を伝えるか」で選べます。選ばなければ商品の最初の訴求で作ります。',
  selectStyle: 'スタイルを選ぶ',
  styleNote: '動画の作り方（絵柄・字幕・構成）です。商品に合わせて台本と絵を作ります。',
  styleModalTitle: '動画のスタイルを選ぶ',
  styleModalLead: '作りたい動画に近いスタイルを1つ選んでください',
  selected: '選択中:',
  cancel: 'キャンセル',
  applyStyle: '適用',
  close: '閉じる',
  create: '動画を作る',
  creating: '開始しています…',
  created: '動画づくりを開始しました',
  creditShort: 'クレジットが足りません。プランかクレジットをご確認ください。',
  listTitle: '作成した動画',
  emptyTitle: 'まだ動画がありません',
  delete: '削除',
  deleteConfirm: (title: string) => `「${title}」を削除しますか？`,
  back: '一覧へ戻る',
  status: {
    pending: '準備中',
    running: '作成中',
    completed: '完成',
    failed: '失敗',
  } as Record<RecipeVideoStatus, string>,
  step: {
    script: '台本を書いています',
    characters: '登場人物を描いています',
    images: 'シーンの絵を描いています',
    voices: '声を作っています',
    bgm: 'BGMを作っています',
    timing: '尺を決めています',
    clips: '動画を作っています',
    compose: '仕上げています',
    done: '完成しました',
  } as Record<RecipeVideoStep, string>,
  progressNote: 'このページを閉じても作成は続きます。完成まで10〜30分ほどかかります。',
  // 一覧の「作成中」区画（段の一覧と今の段の n/m）
  busyTitle: '作成中',
  stagesLabel: '作成の進み具合',
  progressLabel: (title: string) => `${title}の進み具合`,
  stage: {
    script: '台本',
    characters: '登場人物',
    images: 'シーンの絵',
    voices: '声',
    bgm: 'BGM',
    clips: '動画',
    compose: '仕上げ',
  } as Record<RecipeVideoStageKey, string>,
  stageState: {
    done: '済み',
    current: '作成中',
    pending: 'これから',
    failed: '失敗',
  } as Record<RecipeVideoStageState, string>,
  composePhase: {
    queued: '仕上げの順番待ち',
    waiting: 'サーバーの空き待ち',
    frames: '字幕と部品を描いています',
    segments: '映像をつないでいます',
    audio: '声とBGMを合わせています',
    upload: '保存しています',
  } as Record<RecipeVideoComposePhase, string>,
  elapsed: (seconds: number) => {
    const { h, m, s } = splitElapsed(seconds);
    if (h > 0) return `経過 ${h}時間${m}分`;
    return m > 0 ? `経過 ${m}分${pad2(s)}秒` : `経過 ${s}秒`;
  },
  failedTitle: '動画を作れませんでした',
  retry: 'やり直す',
  download: 'ダウンロード',
  seconds: (n: number) => `${n.toFixed(1)}秒`,
  scenesTitle: 'シーン',
  sceneLabel: (i: number) => `シーン ${i + 1}`,
  kind: {
    manga_panel: '漫画のコマ',
    photo_clip: '実写',
    talking_head: '話す人',
    product_ui: '画面紹介',
    endcard: '締めの画面',
  } as Record<string, string>,
  line: 'セリフ（声）',
  caption: '字幕（声と変えるときだけ）',
  captionPlaceholder: '空欄ならセリフと同じ',
  saveLines: 'セリフを保存',
  saved: '保存しました。「変更を反映」で作り直します',
  regenerateImage: '絵を作り直す',
  instructionPlaceholder: '指示（任意）例: もっと明るい表情で',
  regenerateClip: '動きを作り直す',
  marked: '作り直す印を付けました。「変更を反映」で作り直します',
  pendingTitle: '反映していない変更があります',
  pendingDetail: (images: number, voices: number, clips: number, credits: number) =>
    `作り直し: 絵 ${images}・声 ${voices}・動画 ${clips}（目安 ${credits.toLocaleString()} クレジット）。字幕だけの変更は無料です。`,
  apply: '変更を反映して作り直す',
  applied: '作り直しを開始しました',
  busyEdit: '作成中は編集できません',
  noVideoYet: '完成するとここで再生できます',
  // 本格編集（チャットv2 の動画編集）へ渡す導線
  fullEdit: {
    open: '本格編集',
    opening: '開いています…',
    staleTitle: '動画レシピで作り直した内容があります',
    staleBody: '最新の動画で作り直すと、前の本格編集での変更は引き継がれません。',
    rebuild: '最新の動画で作り直す',
    keep: '前の編集を開く',
    failed: '本格編集を開けませんでした',
  },
};

type Strings = typeof JA;

const EN: Strings = {
  pageTitle: 'Style Videos (Beta)',
  createTitle: 'Pick a product and a style to make a video',
  createLead: 'AI writes the script and makes the images, voice, BGM and captions. You can edit each scene afterwards.',
  product: 'Product',
  productPlaceholder: 'Select a product',
  noProducts: 'No products yet. Please register a product first.',
  style: 'Video style',
  noStyles: 'No styles are published yet.',
  scenes: (n: number) => `${n} scenes`,
  approxSeconds: (n: number) => `~${n}s+`,
  estimate: (n: number) => `About ${n.toLocaleString()} credits`,
  appeal: 'Appeal (optional)',
  appealAuto: 'Auto (first appeal of the product)',
  productInfo: 'Product',
  selectProduct: 'Select product',
  changeProduct: 'Change product',
  appealLabel: 'Who and what to tell',
  appealNote: 'Choose it in "Who and what to tell" on the product picker. If not chosen, the first appeal of the product is used.',
  selectStyle: 'Choose style',
  styleNote: 'How the video is made (art, captions, structure). The script and images are made for your product.',
  styleModalTitle: 'Choose a video style',
  styleModalLead: 'Pick the style closest to the video you want',
  selected: 'Selected:',
  cancel: 'Cancel',
  applyStyle: 'Apply',
  close: 'Close',
  create: 'Make video',
  creating: 'Starting…',
  created: 'Video generation started',
  creditShort: 'Not enough credits. Please check your plan or credits.',
  listTitle: 'Your videos',
  emptyTitle: 'No videos yet',
  delete: 'Delete',
  deleteConfirm: (title: string) => `Delete "${title}"?`,
  back: 'Back to list',
  status: {
    pending: 'Queued',
    running: 'In progress',
    completed: 'Done',
    failed: 'Failed',
  },
  step: {
    script: 'Writing the script',
    characters: 'Drawing the characters',
    images: 'Drawing the scenes',
    voices: 'Recording the voices',
    bgm: 'Making the BGM',
    timing: 'Timing the scenes',
    clips: 'Making the video clips',
    compose: 'Finishing',
    done: 'Done',
  },
  progressNote: 'Generation continues even if you close this page. It takes about 10–30 minutes.',
  busyTitle: 'In progress',
  stagesLabel: 'Progress steps',
  progressLabel: (title: string) => `${title} progress`,
  stage: {
    script: 'Script',
    characters: 'Characters',
    images: 'Scenes',
    voices: 'Voices',
    bgm: 'BGM',
    clips: 'Clips',
    compose: 'Finish',
  },
  stageState: {
    done: 'done',
    current: 'in progress',
    pending: 'to do',
    failed: 'failed',
  },
  composePhase: {
    queued: 'Waiting for the finishing queue',
    waiting: 'Waiting for server capacity',
    frames: 'Drawing captions and overlays',
    segments: 'Joining the footage',
    audio: 'Mixing voices and BGM',
    upload: 'Saving',
  },
  elapsed: (seconds: number) => {
    const { h, m, s } = splitElapsed(seconds);
    if (h > 0) return `${h}h ${m}m elapsed`;
    return m > 0 ? `${m}m ${pad2(s)}s elapsed` : `${s}s elapsed`;
  },
  failedTitle: 'The video could not be made',
  retry: 'Retry',
  download: 'Download',
  seconds: (n: number) => `${n.toFixed(1)}s`,
  scenesTitle: 'Scenes',
  sceneLabel: (i: number) => `Scene ${i + 1}`,
  kind: {
    manga_panel: 'Manga panel',
    photo_clip: 'Live action',
    talking_head: 'Talking person',
    product_ui: 'Product screen',
    endcard: 'End card',
  },
  line: 'Line (voice)',
  caption: 'Caption (only if different from the line)',
  captionPlaceholder: 'Same as the line when empty',
  saveLines: 'Save lines',
  saved: 'Saved. Press "Apply changes" to rebuild.',
  regenerateImage: 'Redraw image',
  instructionPlaceholder: 'Direction (optional) e.g. a brighter smile',
  regenerateClip: 'Remake motion',
  marked: 'Marked for remaking. Press "Apply changes" to rebuild.',
  pendingTitle: 'You have changes that are not applied yet',
  pendingDetail: (images: number, voices: number, clips: number, credits: number) =>
    `To remake: ${images} images, ${voices} voices, ${clips} clips (about ${credits.toLocaleString()} credits). Caption-only changes are free.`,
  apply: 'Apply changes and rebuild',
  applied: 'Rebuilding started',
  busyEdit: 'You cannot edit while the video is being made',
  noVideoYet: 'The video will play here when it is done',
  fullEdit: {
    open: 'Advanced edit',
    opening: 'Opening…',
    staleTitle: 'This video was remade in the recipe editor',
    staleBody: 'Rebuilding from the latest video discards the changes made in the previous advanced edit.',
    rebuild: 'Rebuild from the latest video',
    keep: 'Open the previous edit',
    failed: 'Could not open the advanced editor',
  },
};

export const RECIPE_VIDEO_STRINGS: Record<MetaLang, Strings> = { ja: JA, en: EN };

export function useRecipeVideoStrings(): Strings {
  const [lang] = useMetaLang();
  return RECIPE_VIDEO_STRINGS[lang];
}
